import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { isEqual, isPast } from 'date-fns';
import { useEffect } from 'react';

import type { Sport } from '#src/__generated__/globalTypes.ts';
import { ConversionCreditStatus } from '#src/__generated__/globalTypes.ts';
import type { CurrentUserProvider_sportConversionCredits } from '#src/contexts/currentUser/__generated__/queries.graphql.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { currentUserConversionCredits } from '#src/contexts/currentUser/queries.ts';
import { useLazyQuery } from '#src/hooks/graphql/useLazyQuery.ts';
import type { MonetaryAmount } from '#src/lib/monetaryAmount.ts';

import type {
  RefreshSportConversionCreditsQuery,
  RefreshSportConversionCreditsQueryVariables,
} from './__generated__/useConversionCredits.graphql.ts';
import { Sum, useMonetaryAmount } from './useMonetaryAmount.ts';

export type ConversionCreditWithAmounts = Omit<
  NonNullable<
    CurrentUserProvider_sportConversionCredits['sportConversionCredits']
  >['nodes'][0],
  'maxDiscount' | 'totalDiscount'
> & {
  maxDiscount: MonetaryAmount;
  totalDiscount: MonetaryAmount;
  disabled: boolean;
};

const allowedStatuses: ConversionCreditStatus[] = [
  ConversionCreditStatus.CLAIMED,
  ConversionCreditStatus.RECLAIMED,
  ConversionCreditStatus.CREATED,
  ConversionCreditStatus.USING,
];

const REFRESH_CONVERSION_CREDITS_QUERY = gql`
  query RefreshSportConversionCreditsQuery {
    currentUser {
      slug
      ...CurrentUserProvider_sportConversionCredits
    }
  }
  ${currentUserConversionCredits}
` as TypedDocumentNode<
  RefreshSportConversionCreditsQuery,
  RefreshSportConversionCreditsQueryVariables
>;

export const useConversionCredits = ({
  currentlyUsedConversionCredit,
  sport,
  forceRefetch,
}: {
  currentlyUsedConversionCredit?: ConversionCreditWithAmounts;
  sport?: Sport;
  forceRefetch?: boolean;
} = {}): {
  conversionCredits: ConversionCreditWithAmounts[] | undefined;
  groupedConversionCredits: Record<number, ConversionCreditWithAmounts[]>;
  loading: boolean;
} => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const { currentUser } = useCurrentUserContext();
  const hasUsableCredits =
    (currentUser?.conversionCreditsMaxDiscount?.eurCents ?? 0) > 0;
  const willFetch = forceRefetch || hasUsableCredits;
  const [fetchConversionCredits, { data, loading, called }] = useLazyQuery(
    REFRESH_CONVERSION_CREDITS_QUERY,
    { fetchPolicy: forceRefetch ? 'network-only' : 'cache-and-network' }
  );

  useEffect(() => {
    if (willFetch) fetchConversionCredits();
  }, [willFetch, fetchConversionCredits]);

  const conversionCredits =
    data?.currentUser?.sportConversionCredits?.nodes || [];

  const filteredConversionCredit = conversionCredits.reduce<
    ConversionCreditWithAmounts[]
  >((acc, conversionCredit_) => {
    const conversionCredit = conversionCredit_;

    if (
      !allowedStatuses.includes(conversionCredit.status) ||
      isPast(conversionCredit.endDate) ||
      (sport && conversionCredit.sport && sport !== conversionCredit.sport)
    ) {
      return acc;
    }
    acc.push({
      ...conversionCredit,
      disabled:
        conversionCredit.status === ConversionCreditStatus.USING &&
        conversionCredit.id !== currentlyUsedConversionCredit?.id,
      maxDiscount: toMonetaryAmount(conversionCredit.maxDiscount),
      totalDiscount: toMonetaryAmount(conversionCredit.totalDiscount),
    });
    return acc;
  }, []);

  // Merge credits with the same endDate/percentageDiscount/sport/status
  const mergedConversionCredits = filteredConversionCredit.reduce(
    (acc, credit) => {
      const existingCredit = acc.find(
        c =>
          isEqual(c.endDate, credit.endDate) &&
          c.percentageDiscount === credit.percentageDiscount &&
          c.sport === credit.sport &&
          !credit.singleUse &&
          [
            ConversionCreditStatus.CLAIMED,
            ConversionCreditStatus.RECLAIMED,
          ].includesValue(c.status) &&
          [
            ConversionCreditStatus.CLAIMED,
            ConversionCreditStatus.RECLAIMED,
          ].includesValue(credit.status)
      );
      if (existingCredit) {
        existingCredit.maxDiscount = Sum([
          existingCredit.maxDiscount,
          credit.maxDiscount,
        ]);
        existingCredit.totalDiscount = Sum([
          existingCredit.totalDiscount,
          credit.totalDiscount,
        ]);
        return acc;
      }
      return [...acc, { ...credit }];
    },
    [] as ConversionCreditWithAmounts[]
  );

  // Group credits by percentageDiscount
  const groupedConversionCredits = mergedConversionCredits.reduce<
    Record<number, typeof filteredConversionCredit>
  >((acc, cc) => {
    acc[cc.percentageDiscount] = [...(acc[cc.percentageDiscount] || []), cc];
    return acc;
  }, {});

  return {
    groupedConversionCredits,
    conversionCredits: filteredConversionCredit,
    loading: willFetch && (loading || !called),
  };
};
